import {
    Box,
    Divider,
    Heading,
    Image,
    Stack,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
    useDisclosure,
} from "@caspeco/casper-ui-library";
import ExperienceTimeSlot from "components/experiences/experience-time-slot";
import { findExperienceChains } from "components/experiences/find-experience-chains";
import { SafeHTML } from "components/safe-html";
import { isSameWaitListSlot } from "components/selection-components/select-activity/wait-list-utils";
import TimeSlotGrid from "components/selection-components/select-time/time-set/time-slot-grid";
import WaitlistModal from "components/wait-list/wait-list-modal";
import WaitListTimeSlot from "components/wait-list/wait-list-time-slot";
import { useLocalizedValue } from "helpers/use-localized-value";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { useState } from "react";
import { urlBuilder } from "search-params/url-builder";
import { Experience } from "types/experience";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { TimeSlot as TimeSlotType } from "types/time-slot";
import { isTimeBookable, isTimeWaitListable } from "utils/time-slot-filters";

interface ExperienceTimeSectionProps {
    experience: Experience;
    isFirstExperience: boolean;
}

function ExperienceTimeSection({ experience, isFirstExperience }: ExperienceTimeSectionProps) {
    const [showImage, setShowImage] = useState(true);
    const state = useBookingStateContext();
    const { guests, isBookingRequest } = state;
    const { handleCreateBooking, setState } = useBookingActionsContext();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedWaitListTimes, setSelectedWaitListTimes] = useState<TimeSlotType[]>([]);

    const experienceName = useLocalizedValue(experience.name);
    const experienceDescription = useLocalizedValue(experience.description);
    const { startTimes, chains } = findExperienceChains(experience, guests, isBookingRequest);

    // Same rule as the experience modal: chains and requests take no waitlist.
    const supportsWaitlist =
        experience.activities.length === 1 && !isBookingRequest && !experience.bookingRequestOnly;
    const waitListTimes = supportsWaitlist
        ? experience.activities[0].timeSets
              .flatMap((timeSet) => timeSet.times)
              .filter(
                  (time) =>
                      isTimeWaitListable(time, guests ?? 0) && !isTimeBookable(time, guests ?? 0)
              )
        : [];

    // Build image URL
    const imageReference = experience.imageReference16x9 || experience.imageReference1x1;
    const imageParams: Record<string, string | undefined> = {
        AspectRatio: ImageServiceAspectRatio.SixteenByNine,
        Type: "webp",
    };
    const baseUrl = imageReference
        ? `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${imageReference}`
        : "";
    const experienceImageUrl = baseUrl ? urlBuilder(baseUrl, imageParams) : "";

    const handleTimeClick = (time: TimeSlotType) => {
        const chain = chains.find((c) => c[0].start.isSame(time.start));
        if (!chain) return;

        const times = chain.map((slot) => ({ ruleId: slot.ruleId, start: slot.start }));

        const firstActivityWithMenu = chain.find((slot, activityIndex) => {
            const activity = experience.activities[activityIndex];
            return activity?.timeSets.some((ts) => ts.id === slot.ruleId && ts.menuGuid);
        });
        const isRuleWithMenu = Boolean(firstActivityWithMenu);

        handleCreateBooking?.({ ...state, times, isWaitList: false }, isRuleWithMenu);
    };

    const handleWaitlistClick = (time: TimeSlotType) => {
        if (!isOpen) {
            setState?.((prevState) => ({ ...prevState, isWaitList: true }));
            onOpen();
        }

        const isAlreadySelected = selectedWaitListTimes.some((selectedTime) =>
            isSameWaitListSlot(selectedTime, time)
        );
        const newSelectedTimes = isAlreadySelected
            ? selectedWaitListTimes.filter(
                  (selectedTime) => !isSameWaitListSlot(selectedTime, time)
              )
            : [...selectedWaitListTimes, time];

        setSelectedWaitListTimes(newSelectedTimes);

        if (newSelectedTimes.length === 0) {
            onClose();
            setState?.((prevState) => ({ ...prevState, isWaitList: false }));
        }
    };

    const handleWaitlistModalClose = () => {
        onClose();
        setSelectedWaitListTimes([]);
    };

    const handleWaitlistContinue = () => {
        handleCreateBooking?.({
            ...state,
            times: selectedWaitListTimes,
            isWaitList: true,
        });
    };

    if (startTimes.length === 0 && waitListTimes.length === 0) {
        return null;
    }

    const displayTimes = [...startTimes, ...waitListTimes].sort((a, b) => a.start.diff(b.start));

    return (
        <Stack
            spacing={ThemeSpaceVariable.None}
            mt={isFirstExperience ? ThemeSpaceVariable.Medium : ThemeSpaceVariable.None}
            pb={ThemeSpaceVariable.Large}
            data-experience-id={experience.id}
        >
            {showImage && imageReference && (
                <Image
                    src={experienceImageUrl}
                    alt={experienceName}
                    borderRadius={ThemeSpaceVariable.Small}
                    onError={() => setShowImage(false)}
                    w="100%"
                    mb={ThemeSpaceVariable.Medium}
                />
            )}

            <Heading
                as="h5"
                fontSize={ThemeFontSizeVariable.Large}
                fontFamily={ThemeFontVariable.SubHeader}
                color={ThemeColorVariable.OnBackgroundHeader}
                fontWeight="medium"
            >
                {experienceName}
            </Heading>

            {experienceDescription && (
                <Box
                    fontSize={ThemeFontSizeVariable.Medium}
                    color={ThemeColorVariable.OnSurfaceSubdued}
                    mt={ThemeSpaceVariable.Small}
                >
                    <SafeHTML
                        element="span"
                        options={{ allowedTags: ["br"], allowedSchemes: [] }}
                        html={experienceDescription}
                    />
                </Box>
            )}

            <TimeSlotGrid
                timeSlots={displayTimes.map((time) =>
                    waitListTimes.includes(time) ? (
                        <WaitListTimeSlot
                            key={`waitlist-${time.ruleId}-${time.start.format("HHmm")}`}
                            time={time}
                            // WaitListTimeSlot matches on start alone; keep same-time slots of other rules from lighting up
                            selectedTimes={selectedWaitListTimes.filter(
                                (selectedTime) => selectedTime.ruleId === time.ruleId
                            )}
                            onTimeClick={handleWaitlistClick}
                        />
                    ) : (
                        <ExperienceTimeSlot
                            key={time.start.format("HH:mm")}
                            time={time}
                            onClick={() => handleTimeClick(time)}
                        />
                    )
                )}
            />
            <Divider mt={ThemeSpaceVariable.Large} />
            {waitListTimes.length > 0 && (
                <WaitlistModal
                    isOpen={isOpen}
                    handleWaitlistContinue={handleWaitlistContinue}
                    handleWaitlistModalClose={handleWaitlistModalClose}
                    waitListTimes={waitListTimes}
                    selectedTimes={selectedWaitListTimes}
                    onTimeClick={handleWaitlistClick}
                    ruleName={experienceName}
                />
            )}
        </Stack>
    );
}

export default ExperienceTimeSection;
